import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ status: "checking", attempts: 0 });
  const [info, setInfo] = useState(null);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!session_id) { setState({ status: "missing" }); return; }
    let active = true;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${session_id}`);
        if (!active) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState({ status: "paid" });
          try { await refresh?.(); }
          catch (err) { console.warn("[billing] auth refresh post-payment failed:", err); }
          return;
        }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        if (attempts >= 6) { setState({ status: "timeout" }); return; }
        setTimeout(poll, 2000);
      } catch (err) {
        if (!active) return;
        if (attempts >= 6) setState({ status: "error", error: formatApiError(err) });
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [session_id]); // eslint-disable-line

  const isTeam = info?.package_id === "team_foundational";
  const next = isTeam ? "/dashboard" : "/portal";

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--ink-1)" }} data-testid="billing-success-page">
      <header className="border-b border-[var(--ink-border)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Logo size="md" variant="lockup" linkTo="/" dark />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full rounded-2xl ink-card p-8 text-center">
          {state.status === "checking" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin mx-auto" style={{ color: "var(--brand-secondary)" }} />
              <h2 className="font-display font-black text-2xl text-white mt-5">Confirming payment…</h2>
              <p className="text-sm text-white/60 mt-2">Talking to Stripe — usually under 5 seconds.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: "#16A34A" }}>
                <Check className="h-9 w-9 text-white" />
              </div>
              <h2 className="font-display font-black text-3xl text-white mt-5">You're in.</h2>
              <p className="text-sm text-white/60 mt-2">
                {isTeam ? "Your team plan is active. Renews annually at $299 — locked for life on the first 500." : "Subscription active. Your portal is ready."}
              </p>
              {info?.amount && (
                <div className="mt-4 inline-block rounded-lg px-3 py-1.5 text-xs font-mono-num text-white/70" style={{ backgroundColor: "var(--ink-2)" }}>
                  Receipt · ${info.amount.toFixed(2)} {info.currency?.toUpperCase()}
                </div>
              )}
              <Button onClick={() => nav(next, { replace: true })} className="w-full mt-7 text-white font-bold rounded-full py-6 red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="success-continue">
                {isTeam ? "Open coach console" : "Open my portal"} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </>
          )}
          {(state.status === "timeout" || state.status === "error" || state.status === "expired") && (
            <>
              <AlertCircle className="h-12 w-12 mx-auto text-amber-400" />
              <h2 className="font-display font-black text-2xl text-white mt-5">Payment still processing</h2>
              <p className="text-sm text-white/60 mt-2">
                Stripe may take a moment to settle. Check your email for the receipt — if you don't see it within a few minutes, refresh this page.
              </p>
              <Button onClick={() => window.location.reload()} variant="outline" className="w-full mt-6 border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="success-recheck">
                Re-check status
              </Button>
            </>
          )}
          {state.status === "missing" && (
            <>
              <AlertCircle className="h-12 w-12 mx-auto text-amber-400" />
              <h2 className="font-display font-black text-2xl text-white mt-5">No session found</h2>
              <p className="text-sm text-white/60 mt-2">It looks like you landed here without a Stripe session id.</p>
              <Link to="/pricing"><Button className="w-full mt-6 text-white font-bold rounded-full py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>View pricing</Button></Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
